<script>
  import { createNamespacedHelpers } from 'vuex';
  import Preview from '../plate/Preview';
  import EditLayout from '../plate/EditLayout';
  import RichTextLayout from '../RichTextLayout';
  import AddToCart from '../plate/AddToCart';
  import InputDuplicate from '../plate/InputDuplicate';

  import WidgetCallout from "../widgets/WidgetCallout";
  import WidgetButtonLink from "../widgets/WidgetButtonLink";
  import WidgetCustomCode from "../widgets/WidgetCustomCode";
  import WidgetImageGallery from "../widgets/WidgetImageGallery";
  import WidgetImageGalleryWithText from "../widgets/WidgetImageGalleryWithText";
  import WidgetSingleImageLinkOption from "../widgets/WidgetSingleImageLinkOption";

  const { mapGetters } = createNamespacedHelpers('plate');

  export default {
    name: 'ViewPlate',
    components: {
      Preview,
      EditLayout,
      RichTextLayout,
      AddToCart,
      InputDuplicate,
      WidgetCallout,
      WidgetButtonLink,
      WidgetCustomCode,
      WidgetImageGallery,
      WidgetImageGalleryWithText,
      WidgetSingleImageLinkOption
    },
    props: {
      /**
       * the props defined here will be modified to represent the original data gotten
       * from craft later
       */
      title: {
        type: String,
        required: true,
      },
      content: {
        type: String,
        required: true,
      },
      bulkPricing: {
        type: Array,
        default: () => [],
      },
      freeShipping: {
        type: Array,
        default: () => [],
      },
      csrf: {
        type: String,
        default: ''
      },
      sku: {
        type: String,
        default: ''
      },
      entryId: {
        type: Number,
        default: 0
      },
      letterIds: {
        /** letter collection ids belonging to license plate */
        type: Array,
        required: true
      },
      baseProductIds: {
        /** id belonging to license plate */
        type: Array,
        required: true
      },
      allProducts: {
        /** Array of all the customizable products (for navigation between them) */
        type: Array,
        default: () => []
      },
      /** Sidebar widgets that should appear on the page */
      widgets: {
        type: Array,
        default: () => []
      },
      /** Promotion Categories on customizable product */
      promotionCategories: {
        type: String,
        default: '',
      }
    },
    data() {
      return {
        /** The number of lines of text allowed */
        lines: 1,
        /** The amount of horizontal space allowed for 7 Style #3 Letters */
        space: 12.600000001
      };
    },
    computed: {
      ...mapGetters([
        'getSelectedLetterStyle',
        'getSelectedBaseProduct',
        'getSelectedVariant',
        'getLoadingBase',
        'getLoadingLetters'
      ]),
      getSpecialChars() {
        return this.getSelectedLetterStyle.chars.filter(
          (char) => char.type === 'special'
        );
      },
      bulkPricingText() {
        let text = '';
        if (this.bulkPricing.length > 0) {
          this.bulkPricing.forEach((row) => {
            text += row.discount + '% off ' + row.qty + ' or more<br>';
          });
        }
        return text;
      },
      shippingText() {
        let text = '';
        if (this.freeShipping.length > 0) {
          this.freeShipping.forEach((string) => {
            text +=
              '<h4>' +
              string.heading +
              '</h4><p>' +
              string.note +
              '</p>';
          });
        }
        return text;
      },
    },
    created() {
      // Initialize the product in the store
      this.$store.dispatch('plate/initProduct', {
        baseIds: this.baseProductIds.join(),
        baseType: 'customPlate',
        letterIds: this.letterIds.join()
      });

      // set the entry data into the store
      this.$store.commit('plate/setEntryId', this.entryId);
      this.$store.commit('plate/setAllProducts', this.allProducts);

      // set csrf token in store
      this.$store.commit('plate/setCsrfToken', this.csrf);
    },
    methods: {
      resolveWidget(type) {
        return `Widget${type.charAt(0).toUpperCase()}${type.slice(1)}`;
      }
    }
  };
</script>

<template>
  <div class="product-view--plate js-custom-plate">

    <div class="product-view__main">

      <header class="product-view__header m-mbl">
        <h1 class="product-view__title">{{ title }}</h1>
      </header>

      <Preview
        v-if="getSelectedVariant && getSelectedLetterStyle"
        :variant="getSelectedVariant"
        :letterStyle="getSelectedLetterStyle"
      />

      <div v-else>
        <div class="product-view__display m-plate">
          <div class="letter-display--plate js-custom-plate__display-wrap x-loading">
            <span class="loading" />
          </div>
        </div>
      </div>

      <EditLayout :lines="lines" :space="space" />

      <RichTextLayout>
        <template v-slot:title>{{ title }}</template>

        <template v-slot:content>
          <div v-html="content" />
        </template>

        <template v-if="getSelectedLetterStyle" v-slot:letterCase>
          <strong>Lowercase:</strong>
          Height
          <span>{{ getSelectedLetterStyle.lowercase.height }}</span>" Average Width
          <span>{{ getSelectedLetterStyle.lowercase.width }}</span>"<br />
          <strong>Uppercase:</strong>
          Height
          <span>{{ getSelectedLetterStyle.uppercase.height }}</span>" Average Width
          <span>{{ getSelectedLetterStyle.uppercase.width }}</span>"<br />
          <strong>Style: </strong>
          <span>{{ getSelectedLetterStyle.finish.label }}, {{ getSelectedLetterStyle.style }}</span>
        </template>

        <template v-if="getSelectedLetterStyle" v-slot:legend>
          <li v-for="symbol in getSpecialChars" :key="symbol.id">
            For {{ symbol.class }}, type {{ symbol.key }}
          </li>
        </template>
      </RichTextLayout>

    </div>

    <AddToCart
      v-if="getSelectedVariant && getSelectedLetterStyle"
      :letterStyle="getSelectedLetterStyle"
      :bulkPricing="bulkPricing"
      :sku="sku"
      :title="title"
      :entry-id="entryId"
      bundle-id="3"
      :redirectUrl="'/store/cart'"
      :promotion-categories="promotionCategories"
    >
      <template v-slot:inputs>
        <InputDuplicate
          v-for="index in lines"
          :key="index"
          :variant="getSelectedVariant"
          :letter-style="getSelectedLetterStyle"
          :line-of-text="index"
          :space="space"
        />
      </template>

      <template v-if="bulkPricing.length > 0" v-slot:bulkPricing>
        <div class="product-cart__bulk-details">
          <p>
            Buy in Bulk and Save<br />
            <span v-html="bulkPricingText" />
          </p>
        </div>
      </template>

      <template v-if="freeShipping" v-slot:shipping>
        <div class="shipping" v-html="shippingText" />
      </template>

      <template v-if="widgets" v-slot:widgets>
        <component
          v-for="widget in widgets"
          :key="widget.id"
          :is="resolveWidget(widget.type)"
          v-bind="widget"
        />
      </template>
    </AddToCart>

    <div v-else class="product-view__cart">
      <div class="product-cart--letters x-loading">
        <span class="loading" />
      </div>
    </div>

  </div>
</template>
